import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Sparkles, Zap, MapPin, Star } from "lucide-react";
import { api } from "@/lib/api";
import LeaseDealCard from "@/components/LeaseDealCard";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/b272dcbc-055c-406b-9d98-d453af7b32ac/images/1d7c899eaf8fb45648f245c536c2864d947998a4b5512dd672981dddf5b0dbcd.png";

const BRANDS = ["BMW", "Mercedes-Benz", "Porsche", "Tesla", "Audi", "Lexus", "Genesis", "Cadillac", "Toyota", "Honda", "Ford", "Volvo"];

const TESTIMONIALS = [
  { name: "Marcus T.", role: "Charlotte, NC", quote: "Saved $4,200 vs the local dealer. White-glove delivery to my driveway in 5 days.", rating: 5 },
  { name: "Priya S.", role: "Raleigh, NC", quote: "The AI concierge actually understood what I wanted. Then a real broker closed it.", rating: 5 },
  { name: "Devon R.", role: "Atlanta, GA", quote: "Sourced a 911 GTS I couldn't find anywhere. Worth every penny of the broker fee.", rating: 5 },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [arrivals, setArrivals] = useState([]);

  useEffect(() => {
    api.get("/lease-deals", { params: { featured: true, limit: 6 } })
      .then((r) => setFeatured(r.data.slice(0, 6)))
      .catch(() => {});
    api.get("/authorized-inventory-new-arrivals", { params: { limit: 8 } })
      .then((r) => setArrivals(r.data || []))
      .catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden">
        <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050507] via-[#050507]/70 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050507] via-transparent to-transparent" />

        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-10 w-full grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass mb-7" data-testid="hero-badge">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="label-mono">AI Concierge · Nationwide Broker</span>
            </div>
            <h1 className="font-display font-light text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.95] mb-6">
              Luxury Car<br />
              <span className="text-silver">Buying Made</span><br />
              Simple.
            </h1>
            <p className="text-base md:text-lg text-gray-300 max-w-xl mb-10 leading-relaxed font-light">
              Nationwide auto broker and AI-powered vehicle concierge. Source any car, secure any lease deal, finance with confidence — without ever stepping into a dealership.
            </p>
            <div className="flex flex-wrap gap-3" data-testid="hero-ctas">
              <Link to="/find-my-car" className="group inline-flex items-center gap-2 px-7 py-4 rounded-full silver-gradient text-[#050507] font-bold tracking-wide hover:opacity-90 transition" data-testid="hero-find-my-car">
                Find My Car <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition" />
              </Link>
              <Link to="/lease-deals" className="inline-flex items-center gap-2 px-7 py-4 rounded-full border border-white/20 text-white hover:bg-white/5 transition" data-testid="hero-browse-deals">
                Browse Lease Deals
              </Link>
            </div>
            <div className="flex flex-wrap items-center gap-x-7 gap-y-3 mt-12">
              <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-400" /><span className="text-xs text-gray-400">Licensed NC Broker</span></div>
              <div className="flex items-center gap-2"><Star className="w-4 h-4 text-yellow-400 fill-yellow-400" /><span className="text-xs text-gray-400">4.9 · 280+ Reviews</span></div>
              <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-gray-400" /><span className="text-xs text-gray-400">Nationwide Delivery</span></div>
            </div>
          </div>
        </div>

        {/* recent deals ticker */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-white/5 bg-[#050507]/70 backdrop-blur-md overflow-hidden">
          <div className="flex gap-24 animate-marquee py-3 whitespace-nowrap">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="flex gap-24">
                {["BMW M850i · $1,199/mo", "Tesla Model Y · $489/mo", "Porsche Macan S · $889/mo", "Mercedes GLE 450 · $779/mo", "Genesis GV70 · $589/mo", "Cadillac Escalade · $1,299/mo", "Audi RS6 · $1,499/mo"].map((t) => (
                  <span key={t} className="label-mono text-gray-400 inline-flex items-center gap-3">
                    <Sparkles className="w-3 h-3 text-silver" /> {t}
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brand strip */}
      <section className="py-12 border-y border-white/5">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
          <p className="label-mono text-center mb-6">Sourced From Every Major Brand</p>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
            {BRANDS.map((b) => (
              <span key={b} className="font-display text-lg font-light tracking-tight text-gray-400 hover:text-white transition">{b}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Deals */}
      <section className="py-24" data-testid="featured-deals-section">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
          <div className="flex items-end justify-between mb-12 gap-6 flex-wrap">
            <div>
              <p className="label-mono mb-3">Curated Lease Deals</p>
              <h2 className="font-display text-4xl md:text-5xl font-light tracking-tighter">This Month's Best.</h2>
            </div>
            <Link to="/lease-deals" className="inline-flex items-center gap-2 text-sm text-gray-300 hover:text-white" data-testid="view-all-deals">
              View all deals <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((d) => <LeaseDealCard key={d.id} deal={d} />)}
          </div>
        </div>
      </section>

      {/* New Arrivals — freshly-published imported inventory */}
      {arrivals.length > 0 && (
        <section className="py-20 border-t border-white/5" data-testid="new-arrivals-section">
          <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
            <div className="flex items-end justify-between mb-10 gap-6 flex-wrap">
              <div>
                <p className="label-mono mb-3 inline-flex items-center gap-2"><Sparkles className="w-3.5 h-3.5" /> Just Added</p>
                <h2 className="font-display text-4xl md:text-5xl font-light tracking-tighter">New arrivals.</h2>
              </div>
              <Link to="/inventory" className="inline-flex items-center gap-2 text-sm text-gray-300 hover:text-white" data-testid="view-all-inventory">
                Browse inventory <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="flex gap-5 overflow-x-auto pb-3 -mx-1 px-1 snap-x" data-testid="new-arrivals-rail">
              {arrivals.map((v) => {
                const img = v.image_urls && v.image_urls.length > 0 ? v.image_urls[0] : null;
                return (
                  <Link
                    key={v.id}
                    to={`/inventory/${v.id}`}
                    className="group snap-start shrink-0 w-[280px] bg-[#0A0A0E] border border-[#1A1A24] rounded-2xl overflow-hidden hover:border-white/25 transition-colors"
                    data-testid={`arrival-card-${v.id}`}
                  >
                    <div className="relative aspect-[16/11] bg-[#050507] overflow-hidden">
                      {img ? (
                        <img src={img} alt={`${v.year} ${v.make} ${v.model}`} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-600 text-xs">No photo</div>
                      )}
                      <div className="absolute top-3 left-3 flex gap-1.5 items-center">
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/90 text-black font-bold">NEW</span>
                        {v.image_urls?.length > 1 && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-black/60 text-white border border-white/10 backdrop-blur">{v.image_urls.length} photos</span>
                        )}
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="text-sm font-medium truncate">{v.year} {v.make} {v.model}</div>
                      <div className="text-xs text-gray-500 truncate mb-2">{v.trim} · {v.mileage?.toLocaleString()} mi</div>
                      <div className="flex items-baseline justify-between">
                        <span className="font-display text-xl">${(v.price || 0).toLocaleString()}</span>
                        <span className="text-[11px] text-gray-500 inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {v.location || "—"}</span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* How it works */}
      <section className="py-24 border-t border-white/5">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="label-mono mb-3">Three Steps</p>
            <h2 className="font-display text-4xl md:text-5xl font-light tracking-tighter mb-5">A better way to buy.</h2>
            <p className="text-gray-400 leading-relaxed">No dealership lots. No high-pressure tactics. Just a broker who works for you and an AI that does the research.</p>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-3 gap-5">
            {[
              { n: "01", t: "Tell Us What You Want", d: "Use the AI concierge or fill out Find My Car. We learn your needs, budget, and timeline.", icon: Sparkles },
              { n: "02", t: "We Source & Negotiate", d: "Our nationwide dealer network competes for your business. You see the best offer.", icon: Zap },
              { n: "03", t: "Delivered to You", d: "Sign digitally, optional trade-in pickup, white-glove home delivery. Done.", icon: ShieldCheck },
            ].map((s, i) => (
              <div key={s.n} className={`bg-[#0A0A0E] border border-[#1A1A24] p-7 rounded-2xl animate-fade-up delay-${(i+1)*100}`}>
                <div className="label-mono mb-4">{s.n}</div>
                <s.icon className="w-6 h-6 text-silver mb-4" />
                <div className="font-display text-lg mb-2">{s.t}</div>
                <p className="text-sm text-gray-400 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="py-24 border-t border-white/5" data-testid="reviews-section">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
          <div className="text-center mb-14">
            <p className="label-mono mb-3">Social Proof</p>
            <h2 className="font-display text-4xl md:text-5xl font-light tracking-tighter">Loved by buyers nationwide.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="bg-[#0A0A0E] border border-[#1A1A24] p-7 rounded-2xl">
                <div className="flex gap-0.5 mb-4">
                  {Array.from({ length: t.rating }).map((_, j) => (
                    <Star key={j} className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-200 text-[15px] leading-relaxed mb-6">"{t.quote}"</p>
                <div className="label-mono">{t.name} · {t.role}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-10">
          <div className="glass rounded-3xl p-10 md:p-16 text-center">
            <p className="label-mono mb-4">Ready to start?</p>
            <h2 className="font-display text-4xl md:text-6xl font-light tracking-tighter mb-6">Your next car, sourced for you.</h2>
            <p className="text-gray-400 max-w-xl mx-auto mb-8">Book a 20-minute consultation with a 555 broker — or let the AI Concierge surface options first.</p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/booking" className="px-7 py-4 rounded-full silver-gradient text-[#050507] font-bold tracking-wide" data-testid="cta-book">Book Consultation</Link>
              <Link to="/lease-deals" className="px-7 py-4 rounded-full border border-white/20 hover:bg-white/5">See Lease Deals</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
